import { matrixService } from "../services/matrixService";
import { mediaCacheService } from "../services/mediaCacheService";
import type { MediaFetchOptions } from "../services/mediaCacheService";
import {
    decryptMatrixAttachment,
    getBlobSafeMimeType,
    getEncryptedMediaCacheKey,
} from "./encryptedMedia";
import type { MatrixEncryptedFile } from "./encryptedMedia";

export async function getMatrixMediaBlob(
    src: string,
    encryptedFile?: MatrixEncryptedFile,
    mimeType?: string,
    options?: MediaFetchOptions
): Promise<Blob> {
    const mediaMxc = encryptedFile?.url || src;
    if (!mediaMxc.startsWith('mxc://')) {
        const response = await fetch(mediaMxc, { signal: options?.signal });
        if (!response.ok) {
            throw new Error(`Failed to fetch media: ${response.status}`);
        }
        return response.blob();
    }

    const client = matrixService.getClient();
    if (!client) {
        throw new Error('Matrix client not available');
    }

    const httpUrl = client.mxcUrlToHttp(mediaMxc);
    if (!httpUrl) {
        throw new Error('Failed to convert Matrix media URL');
    }

    const token = client.getAccessToken();
    if (!token) {
        throw new Error('No access token available');
    }

    const downloadedBlob = await mediaCacheService.getOrFetchBlob(mediaMxc, httpUrl, token, options);
    if (!encryptedFile) {
        return downloadedBlob;
    }

    const plaintextCacheKey = getEncryptedMediaCacheKey(encryptedFile, mimeType);
    return mediaCacheService.getOrCreatePlaintextBlob(plaintextCacheKey, async () => {
        return new Blob(
            [await decryptMatrixAttachment(await downloadedBlob.arrayBuffer(), encryptedFile)],
            { type: getBlobSafeMimeType(mimeType) },
        );
    });
}

export function getMatrixMediaSource(src: string, encryptedFile?: MatrixEncryptedFile): string {
    return encryptedFile?.url || src;
}
